/**
 * Built-in palette providers: products + static navigation/commands (v2
 * routes). The map/flow/surface/file providers register from the map
 * feature; the chat feature registers the ask-the-guide fallback. The
 * wiring here also installs the default PaletteAction handlers (navigate,
 * canvas.focus, open-panel, ask-guide, run-command).
 */

import { useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';

import { apiGet } from '../../api/client';
import { queryKeys } from '../../api/queries';
import type { ProductList } from '../../api/types';
import {
  productPath,
  productSettingsPath,
  codebasePath,
} from '../../lib/deepLink';
import { useRouteScope } from '../../lib/routeScope';
import { useChatStore } from '../../stores/chatStore';
import { useUiStore } from '../../stores/uiStore';
import {
  hasPaletteCommand,
  registerPaletteActionHandler,
  registerPaletteCommand,
  registerPaletteProvider,
  runPaletteCommand,
  type PaletteProvider,
  type PaletteResult,
} from './registry';

function matches(query: string, ...haystacks: Array<string | undefined>): boolean {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return true;
  return haystacks.some((h) => h !== undefined && h.toLowerCase().includes(q));
}

export function createProductsProvider(queryClient: QueryClient): PaletteProvider {
  return {
    id: 'products',
    label: 'Products',
    priority: 20,
    async getResults(query, signal) {
      const list = await queryClient.fetchQuery({
        queryKey: queryKeys.products,
        queryFn: () => apiGet<ProductList>('/products', signal),
        staleTime: 30_000,
      });
      return list.products
        .filter((p) => matches(query, p.name, p.description))
        .slice(0, 8)
        .map(
          (p): PaletteResult => ({
            id: p.id,
            label: p.name,
            hint: `${p.repo_count} ${p.repo_count === 1 ? 'repo' : 'repos'}`,
            kind: 'command',
            action: { type: 'navigate', to: productPath(p.id) },
          }),
        );
    },
  };
}

interface NavEntry {
  id: string;
  label: string;
  hint?: string;
  /** the repo settings page — needs both an active product and repo */
  repoSettingsPage?: boolean;
  /** the product home (new chat) — needs an active product */
  productPage?: boolean;
  /** the product settings page — needs an active product */
  productSettingsPage?: boolean;
  globalTo?: string;
  commandId?: string;
}

const NAV_ENTRIES: NavEntry[] = [
  // Stage A grammar: the product home IS the new-chat surface (the old
  // Guide/Product-page pair collapsed into it).
  { id: 'nav-new-chat', label: 'New chat', hint: 'ask about this product', productPage: true },
  { id: 'nav-product-settings', label: 'Go to Product settings', productSettingsPage: true },
  { id: 'nav-repo', label: 'Go to Codebase', hint: 'repo status · settings', repoSettingsPage: true },
  { id: 'nav-products', label: 'All products', globalTo: '/products' },
  // The GLOBAL page is "Account" (rename, owner 2026-08-25 — it collided
  // with the product tab's "Settings"); the route stays /settings.
  { id: 'nav-account', label: 'Go to Account', hint: 'you · API key · default model', globalTo: '/settings' },
  { id: 'cmd-theme', label: 'Toggle theme', hint: 'dark ⇄ light', commandId: 'theme.toggle' },
];

export interface ActiveScope {
  productId: string | null;
  repoId: string | null;
}

export function createNavProvider(getScope: () => ActiveScope): PaletteProvider {
  return {
    id: 'nav',
    label: 'Navigate',
    priority: 90,
    getResults(query) {
      const scope = getScope();
      return NAV_ENTRIES.filter((e) => matches(query, e.label, e.hint))
        .filter((e) => {
          // A command entry is only real while its command is registered:
          // the canvas.mode.* rows outlived the map for a while and pressing
          // Enter on them did nothing at all. Gate on the registry instead
          // of trusting the static table.
          if (e.commandId !== undefined) return hasPaletteCommand(e.commandId);
          if (e.globalTo !== undefined) return true;
          if (e.repoSettingsPage === true) {
            return scope.productId !== null && scope.repoId !== null;
          }
          if (e.productPage === true || e.productSettingsPage === true) {
            return scope.productId !== null;
          }
          return false;
        })
        .map((e): PaletteResult => {
          if (e.commandId !== undefined) {
            return {
              id: e.id,
              label: e.label,
              ...(e.hint !== undefined ? { hint: e.hint } : {}),
              kind: 'command',
              action: { type: 'run-command', commandId: e.commandId },
            };
          }
          const to =
            e.globalTo ??
            (e.repoSettingsPage === true
              ? codebasePath(scope.productId!, scope.repoId!)
              : e.productSettingsPage === true
                ? productSettingsPath(scope.productId!)
                : productPath(scope.productId!));
          return {
            id: e.id,
            label: e.label,
            ...(e.hint !== undefined ? { hint: e.hint } : {}),
            kind: 'command',
            action: { type: 'navigate', to },
          };
        });
    },
  };
}

/**
 * Flows as questions. The map used to let you jump a camera at a flow; now
 * the useful thing a flow name can do is start the conversation about it, so
 * every catalog entry becomes an ask staged into the composer.
 */
export function createFlowAskProvider(
  queryClient: QueryClient,
  getScope: () => ActiveScope,
): PaletteProvider {
  return {
    id: 'flow-ask',
    label: 'Ask about',
    priority: 70,
    getResults(query) {
      const { repoId } = getScope();
      if (repoId === null) return [];
      const flows = queryClient.getQueryData<{ flows?: Record<string, { title?: string }> }>(
        queryKeys.artifact(repoId, 'flows'),
      );
      const entries = Object.entries(flows?.flows ?? {});
      const results: PaletteResult[] = [];
      for (const [id, flow] of entries) {
        const title = typeof flow?.title === 'string' ? flow.title : id;
        if (!matches(query, title, 'ask the guide')) continue;
        results.push({
          id: `flow-ask:${id}`,
          label: `Walk me through ${title}`,
          hint: 'ask the guide',
          kind: 'ask',
          action: { type: 'ask-guide', text: `Walk me through "${title}".` },
        });
        if (results.length >= 8) break;
      }
      return results;
    },
  };
}

/**
 * Install built-in providers, commands, and default action handlers.
 * Mounted once inside the route tree (RootLayout) — needs both router and
 * query-client context. Everything unregisters on unmount (tests stay clean).
 */
export function PaletteWiring() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    const scope = (): ActiveScope => ({
      productId: useRouteScope.getState().productId,
      repoId: useRouteScope.getState().repoId,
    });
    // Where a staged ask lands: the product home, whose composer adopts the
    // draft (an open session's composer adopts it in place).
    const guidePath = () => {
      const s = scope();
      return s.productId !== null ? productPath(s.productId) : null;
    };

    const disposers = [
      registerPaletteProvider(createProductsProvider(queryClient)),
      registerPaletteProvider(createNavProvider(scope)),
      registerPaletteProvider(createFlowAskProvider(queryClient, scope)),

      registerPaletteCommand({ id: 'theme.toggle', run: () => useUiStore.getState().toggleTheme() }),

      registerPaletteActionHandler('navigate', (action) => {
        if (action.type === 'navigate') void navigate(action.to);
      }),
      registerPaletteActionHandler('run-command', (action) => {
        if (action.type === 'run-command') runPaletteCommand(action.commandId);
      }),
      registerPaletteActionHandler('ask-guide', (action) => {
        if (action.type !== 'ask-guide') return;
        const path = guidePath();
        if (path !== null) void navigate(path);
        useChatStore.getState().setComposerDraft(action.text);
      }),
    ];
    return () => {
      for (const dispose of disposers) dispose();
    };
  }, [queryClient, navigate]);

  return null;
}
